

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewLoyaltyLedgerModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        customer_id: '',
        order_id: '',
        trip_id: '',
        type: 'Earn',
        source: 'TripCompletion',
        points: '',
        monetary_value: '',
        remaining_points: '',
        status: 'Pending',
        posted_date: '',
        expires_on: '',
        rule_version: '',
        created_by_process: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [customers, setCustomers] = useState([]);
    const [travelOrders, setTravelOrders] = useState([]);
    const [trips, setTrips] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [loadingTrips, setLoadingTrips] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchCustomers();
            fetchTravelOrders();
            fetchTrips();
        }
    }, [isOpen]);

    const fetchCustomers = async () => {
        setLoadingCustomers(true);
        try {
            const response = await axiosInstance.get('/customers/');
            setCustomers(response.data || []);
        } catch (error) {
            console.error('Error fetching customers:', error);
        } finally {
            setLoadingCustomers(false);
        }
    };

    const fetchTravelOrders = async () => {
        setLoadingOrders(true);
        try {
            const response = await axiosInstance.get('/travel-orders/');
            setTravelOrders(response.data || []);
        } catch (error) {
            console.error('Error fetching travel orders:', error);
        } finally {
            setLoadingOrders(false);
        }
    };

    const fetchTrips = async () => {
        setLoadingTrips(true);
        try {
            const response = await axiosInstance.get('/trips/');
            setTrips(response.data || []);
        } catch (error) {
            console.error('Error fetching trips:', error);
        } finally {
            setLoadingTrips(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Name is required';
        if (!formData.customer_id) errors.customer_id = 'Customer is required';
        if (!formData.type) errors.type = 'Type is required';
        if (!formData.points) errors.points = 'Points is required';
        if (!formData.status) errors.status = 'Status is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                customer_id: parseInt(formData.customer_id),
                order_id: formData.order_id ? parseInt(formData.order_id) : null,
                trip_id: formData.trip_id ? parseInt(formData.trip_id) : null,
                points: parseInt(formData.points),
                monetary_value: formData.monetary_value ? parseFloat(formData.monetary_value) : null,
                remaining_points: formData.remaining_points ? parseInt(formData.remaining_points) : null,
                posted_date: formData.posted_date || null,
                expires_on: formData.expires_on || null,
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            setFormData({
                name: '',
                customer_id: '',
                order_id: '',
                trip_id: '',
                type: 'Earn',
                source: 'TripCompletion',
                points: '',
                monetary_value: '',
                remaining_points: '',
                status: 'Pending',
                posted_date: '',
                expires_on: '',
                rule_version: '',
                created_by_process: ''
            });
        } catch (error) {
            console.error('Error saving Loyalty Ledger:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Loyalty Ledger',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '80%', md: '700px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Loyalty Ledger</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={customers}
                                        getOptionLabel={(option) => option?.name || ''}
                                        loading={loadingCustomers}
                                        value={customers.find(c => c.id === parseInt(formData.customer_id)) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, customer_id: newValue?.id || '' }));
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: '35px' } }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Customer"
                                                variant="outlined"
                                                margin="normal"
                                                required
                                                error={!!formErrors.customer_id}
                                                helperText={formErrors.customer_id}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingCustomers ? <CircularProgress size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={travelOrders}
                                        getOptionLabel={(option) => option?.name || ''}
                                        loading={loadingOrders}
                                        value={travelOrders.find(o => o.id === parseInt(formData.order_id)) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, order_id: newValue?.id || '' }));
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: '35px' } }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Order"
                                                variant="outlined"
                                                margin="normal"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingOrders ? <CircularProgress size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={trips}
                                        getOptionLabel={(option) => option?.name || ''}
                                        loading={loadingTrips}
                                        value={trips.find(t => t.id === parseInt(formData.trip_id)) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, trip_id: newValue?.id || '' }));
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: '35px' } }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Trip"
                                                variant="outlined"
                                                margin="normal"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingTrips ? <CircularProgress size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Type</InputLabel>
                                        <Select
                                            name="type"
                                            value={formData.type}
                                            onChange={handleInputChange}
                                            label="Type"

                                        >
                                            <MenuItem value="Earn">Earn</MenuItem>
                                            <MenuItem value="Redemption">Redemption</MenuItem>
                                            <MenuItem value="Expiry">Expiry</MenuItem>
                                            <MenuItem value="Adjustment">Adjustment</MenuItem>
                                            <MenuItem value="Reversal">Reversal</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Source</InputLabel>
                                        <Select
                                            name="source"
                                            value={formData.source}
                                            onChange={handleInputChange}
                                            label="Source"

                                        >
                                            <MenuItem value="TripCompletion">Trip Completion</MenuItem>
                                            <MenuItem value="Review">Review</MenuItem>
                                            <MenuItem value="Goodwill">Goodwill</MenuItem>
                                            <MenuItem value="Migration">Migration</MenuItem>
                                            <MenuItem value="Birthday Points">Birthday Points</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Points"
                                        name="points"
                                        type="number"
                                        value={formData.points}
                                        onChange={handleInputChange}
                                        error={!!formErrors.points}
                                        helperText={formErrors.points}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            label="Status"

                                        >
                                            <MenuItem value="Pending">Pending</MenuItem>
                                            <MenuItem value="Credited">Credited</MenuItem>
                                            <MenuItem value="Reversed">Reversed</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Monetary Value"
                                        name="monetary_value"
                                        type="number"
                                        value={formData.monetary_value}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Remaining Points"
                                        name="remaining_points"
                                        type="number"
                                        value={formData.remaining_points}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Posted Date"
                                        name="posted_date"
                                        type="datetime-local"
                                        value={formData.posted_date}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Expires On"
                                        name="expires_on"
                                        type="date"
                                        value={formData.expires_on}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Rule Version"
                                        name="rule_version"
                                        value={formData.rule_version}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Created By Process"
                                        name="created_by_process"
                                        value={formData.created_by_process}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Loyalty Ledger'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewLoyaltyLedgerModal;

